import React, { cloneElement, useMemo, useState } from 'react';
import type { Placement } from '@floating-ui/react';
import {
  arrow,
  autoUpdate,
  flip,
  FloatingArrow,
  FloatingPortal,
  offset,
  shift,
  useDismiss,
  useFloating,
  useFocus,
  useHover,
  useInteractions,
  useRole,
} from '@floating-ui/react';

export interface TooltipProps {
  content: React.ReactNode;
  children: React.ReactElement;
  placement?: Placement;
  disabled?: boolean;
  maxWidth?: number | string;
  delay?: number | [number, number];
  className?: string;
}

function Tooltip({
  content,
  children,
  placement = 'top',
  disabled = false,
  maxWidth = 350,
  delay = 200,
  className = '',
}: TooltipProps) {
  const [isOpen, setIsOpen] = useState(false);
  const arrowRef = React.useRef(null);

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement,
    middleware: [
      offset(8),
      flip({
        fallbackAxisSideDirection: 'start',
      }),
      shift({ padding: 8 }),
      arrow({ element: arrowRef }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const delayValue = useMemo(() => {
    if (typeof delay === 'number') {
      return { open: delay, close: delay };
    }
    return { open: delay[0], close: delay[1] };
  }, [delay]);

  const hover = useHover(context, {
    move: false,
    delay: delayValue,
    enabled: !disabled,
  });
  const focus = useFocus(context, { enabled: !disabled });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([hover, focus, dismiss, role]);

  if (disabled || !content) {
    return children;
  }

  return (
    <>
      {cloneElement(
        children,
        getReferenceProps({
          ref: refs.setReference,
          ...(children.props as object),
        }),
      )}
      {isOpen && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{
              ...floatingStyles,
              maxWidth: typeof maxWidth === 'number' ? `${maxWidth}px` : maxWidth,
              zIndex: 9999,
            }}
            className={`rounded bg-gray-900 px-3 py-2 text-sm text-white shadow-lg ${className}`}
            {...getFloatingProps()}
          >
            {content}
            <FloatingArrow ref={arrowRef} context={context} fill="#111827" />
          </div>
        </FloatingPortal>
      )}
    </>
  );
}

export default Tooltip;
